import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { useMusic } from '../../context/MusicContext';
import { FaPlay, FaTrash } from 'react-icons/fa';
import toast from 'react-hot-toast';

const TrackRow = ({ track, onDeleteClick }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { setQueueAndPlay } = useMusic();
  const navigate = useNavigate();

  const rowStyles = track.isDeleted 
    ? "bg-red-50 opacity-75 grayscale-[0.5]" 
    : "hover:bg-gray-50";

  const handlePlay = (e) => {
    e.stopPropagation();
    // Wrap the single track in an array to fulfill the queue requirement
    // We pass 'Admin' as the source for analytics/tracking
    setQueueAndPlay([track], 0, 'Admin');
  };

  const goToProfile = (e) => {
    e.stopPropagation();
    if (track.artistId) {
      navigate(`/profile/${track.artistId}`);
    } else {
      toast.error("No Artist ID found for this track.");
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!track.audioURL) return toast.error("No audio file found.");
    
    setIsDownloading(true);
    const downloadToast = toast.loading("Downloading audio...");

    try {
      const response = await fetch(track.audioURL);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileExtension = track.audioURL.split('.').pop().split('?')[0] || 'mp3';
      
      link.href = url;
      link.download = `${track.artistName} - ${track.title}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.dismiss(downloadToast);
      toast.success("File ready.");
    } catch (error) {
      toast.dismiss(downloadToast);
      toast.error("Download failed.");
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`grid grid-cols-5 gap-4 p-4 border-b items-center transition-colors ${rowStyles}`}>
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <span className="font-bold text-slate-800">{track.title}</span>
          {/* ✅ 2. Add a visual tag for deleted status */}
          {track.isDeleted && (
            <span className="text-[10px] bg-red-200 text-red-700 px-2 py-0.5 rounded-full font-black uppercase">
              Deleted
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400 font-mono truncate">{track.id}</span>
      </div>

      {/* Artist Link */}
      <div className="flex">
        <button 
          onClick={goToProfile}
          className="text-blue-600 font-medium hover:underline hover:text-blue-800 transition-colors text-left"
        >
          {track.artistName}
        </button>
      </div>

      <span className="text-gray-500 text-sm">
        {track.uploadedAt ? track.uploadedAt.toDate().toLocaleDateString() : 'N/A'}
      </span>

      {/* ✅ NEW DELETE COLUMN */}
      <div className="flex justify-center">
        {/* ✅ 3. Hide delete button if already deleted */}
        {!track.isDeleted && (
          <button 
            onClick={(e) => { e.stopPropagation(); onDeleteClick(track); }}
            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
          >
            <FaTrash size={14} />
          </button>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        {/* Play Button */}
        <button
          onClick={handlePlay}
          className="p-3 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-600 hover:text-white transition-all active:scale-90"
          title="Preview Track"
        >
          <FaPlay className="text-xs" />
        </button>

        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="bg-slate-800 text-white font-bold text-xs px-4 py-2.5 rounded-lg hover:bg-black disabled:bg-gray-300 transition-all uppercase tracking-tighter"
        >
          {isDownloading ? "Wait..." : "Download"}
        </button>
      </div>
    </div>
  );
};

const TracksTab = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeletedOnly, setShowDeletedOnly] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const db = getFirestore();
  const functions = getFunctions(undefined, 'us-central1');

  useEffect(() => {
    const q = query(
      collection(db, 'songs'), 
      where('isDeleted', '==', showDeletedOnly), 
      orderBy('uploadedAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTracks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [db, showDeletedOnly]);

  const handleHardDelete = async () => {
    if (!confirmingDelete) return;
    setIsDeleting(true);

    try {
      const deleteSong = httpsCallable(functions, 'deleteSong');
      await deleteSong({ songId: confirmingDelete.id });
      toast.success("Song deleted successfully.");
      setConfirmingDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete track.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Fetching library...</div>;

  return (
    <div className="p-6 relative">
      {/* --- Tab Switcher --- */}
      <div className="flex justify-between items-end mb-6">
        <div className="flex gap-x-1 bg-gray-100 p-1 rounded-lg w-fit">
          <button 
            onClick={() => setShowDeletedOnly(false)} 
            className={`px-6 py-2 rounded-md text-sm font-bold transition-all ${!showDeletedOnly ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Active Tracks
          </button>
          <button 
            onClick={() => setShowDeletedOnly(true)} 
            className={`px-6 py-2 rounded-md text-sm font-bold transition-all ${showDeletedOnly ? 'bg-white shadow text-red-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Deleted Tracks
          </button>
        </div>

        {/* Optional: Counter */}
        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
          Showing: {tracks.length} {showDeletedOnly ? 'Deleted' : 'Active'} Items
        </span>
      </div>
      {/* --- Confirmation Modal --- */}
      {confirmingDelete && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-slate-800">Delete Track?</h3>
            <p className="text-sm text-gray-500 mt-2">
              Are you sure you want to delete <span className="font-bold text-slate-800">{confirmingDelete.title}</span>? 
              This will remove the song from the platform for 90 days before permanent purging.
            </p>
            <div className="flex gap-3 mt-6">
              <button 
                disabled={isDeleting}
                onClick={() => setConfirmingDelete(null)}
                className="flex-1 px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                disabled={isDeleting}
                onClick={handleHardDelete}
                className="flex-1 px-4 py-2 text-sm font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-lg"
              >
                {isDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* ✅ Updated to grid-cols-5 */}
        <div className="grid grid-cols-5 gap-4 p-4 bg-gray-50 border-b text-xs font-black uppercase tracking-widest text-gray-400">
          <span>Song Title / ID</span>
          <span>Artist</span>
          <span>Upload Date</span>
          <span className="text-center">Delete</span>
          <span className="text-right">Actions</span>
        </div>
        
        {tracks.length > 0 ? (
          tracks.map(track => (
            <TrackRow 
              key={track.id} 
              track={track} 
              onDeleteClick={(t) => setConfirmingDelete(t)} 
            />
          ))
        ) : (
          <div className="p-10 text-center text-gray-400 italic">No tracks found in library.</div>
        )}
      </div>
    </div>
  );
};

export default TracksTab;